import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Plus, 
  Users, 
  FileText, 
  Clock, 
  Youtube, 
  Mic, 
  Hash,
  Loader2,
  Eye,
  EyeOff,
  Trash2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { sessionService, SessionContextItem } from '../services/sessionService';
import { useAuth } from '../services/AuthContext';

interface SessionContextManagerProps {
  sessionId: string;
  sessionContext: SessionContextItem[];
  onSessionContextUpdate: (sessionContext: SessionContextItem[]) => void;
  className?: string;
}

interface SessionSearchItem {
  sessionId: string;
  filename: string;
  title: string;
  isYouTubeMode: boolean;
  youtubeUrl?: string;
  speakerCount: number;
  speakerNames: string[];
  wordCount: number;
  createdAt: Date;
  updatedAt: Date;
  timeAgo: string;
}

const SessionContextManager: React.FC<SessionContextManagerProps> = ({ 
  sessionId, 
  sessionContext, 
  onSessionContextUpdate,
  className = '' 
}) => {
  const { token } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<SessionSearchItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isAddingSession, setIsAddingSession] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSessions = useCallback(async (search?: string) => {
    if (!token) return;
    
    try {
      setIsSearching(!!search);
      const sessions = await sessionService.listSessions(token, search, 10);
      
      // Filter out current session and already added sessions
      const filteredSessions = sessions.filter(session => 
        session.sessionId !== sessionId && 
        !sessionContext.some(ctx => ctx.sessionId === session.sessionId)
      );
      
      setSearchResults(filteredSessions);
      setError(null);
    } catch (err) {
      console.error('Failed to load sessions:', err);
      setError('Failed to load sessions');
    } finally {
      setIsSearching(false);
    }
  }, [token, sessionId, sessionContext]);

  useEffect(() => {
    if (isExpanded) {
      const timeoutId = setTimeout(() => {
        if (searchTerm) {
          loadSessions(searchTerm);
        } else {
          loadSessions();
        }
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm, loadSessions, isExpanded]);

  const formatWordCount = (count: number): string => {
    if (count < 1000) return `${count} words`;
    if (count < 1000000) return `${(count / 1000).toFixed(1)}k words`;
    return `${(count / 1000000).toFixed(1)}M words`;
  };

  const truncateId = (id: string): string => {
    return `${id.substring(0, 8)}...`;
  };

  const addSessionToContext = async (searchSession: SessionSearchItem) => {
    if (!token) return;
    
    setIsAddingSession(true);
    try {
      const response = await sessionService.addSessionToContext(sessionId, searchSession.sessionId, token);
      if (response.success) {
        const updatedContext = [...sessionContext, response.contextItem];
        onSessionContextUpdate(updatedContext);
        
        // Remove from search results
        setSearchResults(prev => prev.filter(s => s.sessionId !== searchSession.sessionId));
        setError(null);
      }
    } catch (err) {
      console.error('Failed to add session to context:', err);
      setError(`Failed to add session: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsAddingSession(false);
    }
  };

  const removeSessionFromContext = async (contextSessionId: string) => {
    if (!token) return;
    
    try {
      const response = await sessionService.removeSessionFromContext(sessionId, contextSessionId, token);
      if (response.success) {
        const updatedContext = sessionContext.filter(ctx => ctx.sessionId !== contextSessionId);
        onSessionContextUpdate(updatedContext);
        
        // Refresh search results to show the removed session again
        if (isExpanded) {
          loadSessions(searchTerm || undefined);
        }
        setError(null);
      }
    } catch (err) {
      console.error('Failed to remove session from context:', err);
      setError(`Failed to remove session: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const toggleSessionInContext = async (contextSessionId: string, includedInContext: boolean) => {
    if (!token) return;
    
    try {
      const response = await sessionService.toggleSessionContextInclusion(sessionId, contextSessionId, includedInContext, token);
      if (response.success) {
        const updatedContext = sessionContext.map(ctx => 
          ctx.sessionId === contextSessionId 
            ? { ...ctx, includedInContext } 
            : ctx
        );
        onSessionContextUpdate(updatedContext);
        setError(null);
      }
    } catch (err) {
      console.error('Failed to toggle session context inclusion:', err);
      setError(`Failed to update context: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const enabledContextCount = sessionContext.filter(ctx => ctx.includedInContext).length;

  return (
    <div className={`border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 ${className}`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
      >
        <div className="flex items-center space-x-3">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Session Context</span>
          {sessionContext.length > 0 && (
            <div className="flex items-center space-x-1 text-xs text-gray-500">
              <span>{enabledContextCount}/{sessionContext.length} sessions in context</span>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-3 pt-0 space-y-4">
              {/* Description */}
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Include other sessions in your chat context. Their transcripts and TTML files will be available to the AI for analysis and comparison.
              </div>

              {/* Current Session Context */}
              {sessionContext.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Sessions in Context ({sessionContext.length})
                    </label>
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      {enabledContextCount} enabled
                    </span>
                  </div>
                  
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {sessionContext.map((ctx) => (
                      <motion.div
                        key={ctx.sessionId}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className={`p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 transition-all ${
                          ctx.includedInContext 
                            ? 'border-primary-300 dark:border-primary-600 bg-primary-50 dark:bg-primary-900/20' 
                            : 'opacity-60'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            {/* Title and Filename */}
                            <div className="flex items-center space-x-2 mb-2">
                              {ctx.isYouTubeMode ? (
                                <Youtube className="h-3 w-3 text-red-500 flex-shrink-0" />
                              ) : (
                                <Mic className="h-3 w-3 text-primary-600 flex-shrink-0" />
                              )}
                              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                {ctx.title}
                              </h4>
                            </div>

                            {ctx.filename !== ctx.title && (
                              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 truncate">
                                {ctx.filename}
                              </p>
                            )}

                            {/* Stats */}
                            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                              <div className="flex items-center space-x-1">
                                <Hash className="h-2 w-2" />
                                <span>{truncateId(ctx.sessionId)}</span>
                              </div>
                              
                              {ctx.speakerCount > 0 && (
                                <div className="flex items-center space-x-1">
                                  <Users className="h-2 w-2" />
                                  <span>{ctx.speakerCount} speaker{ctx.speakerCount !== 1 ? 's' : ''}</span>
                                </div>
                              )}

                              <div className="flex items-center space-x-1">
                                <FileText className="h-2 w-2" />
                                <span>{formatWordCount(ctx.wordCount)}</span>
                              </div>
                            </div>

                            {/* Speaker Names */}
                            {ctx.speakerNames.length > 0 && (
                              <div className="mt-2">
                                <div className="flex flex-wrap gap-1">
                                  {ctx.speakerNames.slice(0, 2).map((name, index) => (
                                    <span
                                      key={index}
                                      className="inline-block px-1.5 py-0.5 text-xs bg-primary-100 dark:bg-primary-900 
                                               text-primary-800 dark:text-primary-200 rounded-full"
                                    >
                                      {name}
                                    </span>
                                  ))}
                                  {ctx.speakerNames.length > 2 && (
                                    <span className="inline-block px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 
                                                   text-gray-600 dark:text-gray-400 rounded-full">
                                      +{ctx.speakerNames.length - 2}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Controls */}
                          <div className="ml-3 flex items-center space-x-1">
                            <button
                              onClick={() => toggleSessionInContext(ctx.sessionId, !ctx.includedInContext)}
                              className={`p-1 rounded transition-colors ${
                                ctx.includedInContext
                                  ? 'text-brand-yellow hover:bg-[#fff3d6] dark:hover:bg-brand-yellow/15'
                                  : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                              title={ctx.includedInContext ? "Exclude from context" : "Include in context"}
                            >
                              {ctx.includedInContext ? <Eye size={14} /> : <EyeOff size={14} />}
                            </button>
                            
                            <button
                              onClick={() => removeSessionFromContext(ctx.sessionId)}
                              className="p-1 text-red-400 hover:bg-red-100 dark:hover:bg-red-900/20 rounded transition-colors"
                              title="Remove from context list"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add Sessions Section */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Add Sessions to Context
                </label>
                
                {/* Search Bar */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by filename, title, session ID, or speaker name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-8 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg 
                             bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 
                             placeholder-gray-500 dark:placeholder-gray-400
                             focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                             transition-all duration-200"
                  />
                  {isSearching && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <Loader2 className="animate-spin h-3 w-3 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {searchResults.map((session) => (
                      <motion.div
                        key={session.sessionId}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="group cursor-pointer"
                      >
                        <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 
                                      hover:border-primary-300 dark:hover:border-primary-600
                                      hover:bg-primary-50 dark:hover:bg-primary-900/20
                                      transition-all duration-200">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              {/* Title and Filename */}
                              <div className="flex items-center space-x-2 mb-1">
                                {session.isYouTubeMode ? (
                                  <Youtube className="h-3 w-3 text-red-500 flex-shrink-0" />
                                ) : (
                                  <Mic className="h-3 w-3 text-primary-600 flex-shrink-0" />
                                )}
                                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {session.title}
                                </h4>
                              </div>

                              {session.filename !== session.title && (
                                <p className="text-xs text-gray-600 dark:text-gray-400 mb-1 truncate">
                                  {session.filename}
                                </p>
                              )}

                              {/* Stats */}
                              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                                <div className="flex items-center space-x-1">
                                  <Hash className="h-2 w-2" />
                                  <span>{truncateId(session.sessionId)}</span>
                                </div>
                                
                                <div className="flex items-center space-x-1">
                                  <Clock className="h-2 w-2" />
                                  <span>{session.timeAgo}</span>
                                </div>

                                {session.speakerCount > 0 && (
                                  <div className="flex items-center space-x-1">
                                    <Users className="h-2 w-2" />
                                    <span>{session.speakerCount} speaker{session.speakerCount !== 1 ? 's' : ''}</span>
                                  </div>
                                )}

                                <div className="flex items-center space-x-1">
                                  <FileText className="h-2 w-2" />
                                  <span>{formatWordCount(session.wordCount)}</span>
                                </div>
                              </div>

                              {/* Speaker Names */}
                              {session.speakerNames.length > 0 && (
                                <div className="mt-1">
                                  <div className="flex flex-wrap gap-1">
                                    {session.speakerNames.slice(0, 2).map((name, index) => (
                                      <span
                                        key={index}
                                        className="inline-block px-1.5 py-0.5 text-xs bg-primary-100 dark:bg-primary-900 
                                                 text-primary-800 dark:text-primary-200 rounded-full"
                                      >
                                        {name}
                                      </span>
                                    ))}
                                    {session.speakerNames.length > 2 && (
                                      <span className="inline-block px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 
                                                     text-gray-600 dark:text-gray-400 rounded-full">
                                        +{session.speakerNames.length - 2}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Add Button */}
                            <div className="ml-3 flex-shrink-0">
                              <button
                                onClick={() => addSessionToContext(session)}
                                disabled={isAddingSession}
                                className="flex items-center space-x-1 px-2 py-1 bg-primary-600 text-white rounded text-xs 
                                         hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed 
                                         transition-colors"
                                title="Add to context"
                              >
                                {isAddingSession ? (
                                  <Loader2 className="animate-spin h-3 w-3" />
                                ) : (
                                  <Plus size={12} />
                                )}
                                <span>Add</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}

                {searchResults.length === 0 && !isSearching && searchTerm && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    <p className="text-sm">No sessions found matching "{searchTerm}"</p>
                  </div>
                )}

                {searchResults.length === 0 && !isSearching && !searchTerm && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    <p className="text-sm">Search for sessions to add to context</p>
                  </div>
                )}
              </div>

              {/* Error Display */}
              {error && (
                <div className="text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 p-2 rounded">
                  {error}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SessionContextManager;
